<!--
  Redesigned model form, used by the dev-model edit page.

  Same public contract as BreadTabulatorForm — `setData()`, `setDataProperty()`,
  the `action`/`api`/`model`/`parentModel`/`isLoading` props — and the SAME
  submit payload: the field coercions, the `_count` strip, the parent-relation
  strip and the leadNew branch are carried over verbatim, so nothing about what
  reaches the API changes. Only the layout does.

  What changed:
    · fields sit in a responsive two-column grid instead of one tall stack, so a
      four-field record no longer occupies a full screen of scrolling
    · every input — including the ones the shared BreadTabulatorFormSelectModel
      family renders — gets one field chrome: the same outlined, dense,
      details-free box, asked for by prop so those components stay shared
      rather than forked, and so the add / child-add forms that still want the
      underline select are unaffected
    · required fields are marked in the label
    · a skeleton stands in while the record loads, instead of the whole card
      popping in at once
    · the actions are a footer bar, with the destructive-to-lose action (Cancel)
      subordinate to Save

  BreadTabulatorForm is untouched and still serves the add / child-add pages.
-->
<template>
  <div class="bmf">
    <AError :api="api"></AError>

    <v-form ref="form" lazy-validation @submit.prevent="validate">
      <v-card class="bmf__card" :class="{ 'bmf__card--embedded': embedded }" :flat="embedded">
        <div v-if="!embedded" class="bmf__head">
          <div>
            <h2 class="bmf__title">{{ sectionTitle }}</h2>
            <p class="bmf__subtitle">
              {{ isLoading ? "Loading record…" : subtitle }}
            </p>
          </div>
        </div>

        <!-- Skeleton mirrors the real grid so the card doesn't resize on load. -->
        <div v-if="isLoading" class="bmf__body">
          <div class="bmf__grid">
            <div v-for="n in skeletonCount" :key="n" class="bmf__field">
              <v-skeleton-loader type="text, text" class="bmf__skeleton" />
            </div>
          </div>
        </div>

        <div v-else class="bmf__body">
          <div class="bmf__grid">
            <div
              v-for="(field, index) in form.fields"
              :key="index"
              class="bmf__field"
              :class="{
                'bmf__field--wide': isWide(index),
                'bmf__field--check': field.type == 'boolean',
                'bmf__field--inner-label': !hasOwnLabel(field),
              }"
            >
              <!-- The label is ours rather than Vuetify's floating one: a
                   floated label is a notch cut into the field's own border, and
                   at this font size it read as a line struck through the text.
                   Sitting above the box it also gives the required marker a
                   place to live that isn't inside the control. The multi-input
                   field types are the exception — they carry two controls with
                   a name each, so those keep their own labels. -->
              <label v-if="hasOwnLabel(field)" class="bmf__label">
                {{ field.name }}
                <span v-if="isRequired(field.key)" class="bmf__req">*</span>
              </label>

              <BreadTabulatorFormSelectModel
                v-if="field.type == 'model'"
                :form="form"
                :field="field"
                @input="form.data[field.key] = $event"
                :rules="rulesFor(field.key)"
                :disabled="isParentLockedField(field)"
                label=""
                outlined
                dense
                hide-details="auto"
              ></BreadTabulatorFormSelectModel>

              <BreadTabulatorFormDependanceSelectModel
                v-else-if="field.type == 'modelDependency'"
                :form="form"
                :field="field"
                @inputParent="form.data[field.parentKey] = $event"
                @inputchild="form.data[field.childKey] = $event"
                :parentRules="rulesFor(field.parentKey)"
                :childRules="rulesFor(field.childKey)"
                outlined
                dense
                hide-details="auto"
              ></BreadTabulatorFormDependanceSelectModel>

              <BreadTabulatorFormParentSelectModel
                v-else-if="field.type == 'modelParent'"
                :form="form"
                :field="field"
                @inputParent="form.data[field.parentKey] = $event"
                :parentRules="rulesFor(field.parentKey)"
                @getParentSelectedItem="getParentSelectedItem"
                outlined
                dense
                hide-details="auto"
              ></BreadTabulatorFormParentSelectModel>

              <BreadTabulatorFormChildSelectModel
                v-else-if="field.type == 'modelChild'"
                :form="form"
                :field="field"
                @inputchild="form.data[field.childKey] = $event"
                :childRules="rulesFor(field.childKey)"
                :parentSelectedValue="parentSelectedValue"
                outlined
                dense
                hide-details="auto"
              ></BreadTabulatorFormChildSelectModel>

              <v-checkbox
                v-else-if="field.type == 'boolean'"
                v-model="form.data[field.key]"
                :label="labelFor(field)"
                :rules="rulesFor(field.key)"
                class="bmf__check"
                hide-details="auto"
              ></v-checkbox>

              <v-select
                v-else-if="field.type == 'select'"
                v-model="form.data[field.key]"
                :items="field.options"
                item-text="text"
                item-value="value"
                :rules="rulesFor(field.key)"
                outlined
                dense
                hide-details="auto"
              ></v-select>

              <v-textarea
                v-else-if="field.type == 'textarea'"
                v-model="form.data[field.key]"
                :rules="rulesFor(field.key)"
                outlined
                rows="4"
                hide-details="auto"
              ></v-textarea>

              <v-text-field
                v-else-if="field.type == 'float'"
                v-model="form.data[field.key]"
                :rules="rulesFor(field.key)"
                type="number"
                step="0.01"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>

              <v-text-field
                v-else
                v-model="form.data[field.key]"
                :type="field.type"
                :max="field.max ? field.max : false"
                :min="field.min ? field.min : false"
                :rules="rulesFor(field.key)"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </div>
          </div>
        </div>

        <div v-if="!embedded" class="bmf__actions">
          <span class="bmf__hint">
            <template v-if="requiredCount">
              <span class="bmf__req">*</span> required
            </template>
          </span>
          <button class="bmf__btn bmf__btn--ghost" type="button" @click="$router.go(-1)">
            Cancel
          </button>
          <v-btn
            class="bmf__btn bmf__btn--primary"
            depressed
            :disabled="isLoading"
            :loading="api.isLoading"
            @click="validate"
          >
            {{ submitLabel }}
          </v-btn>
        </div>
      </v-card>
    </v-form>
  </div>
</template>

<script>
import BreadTabulatorFormSelectModel from "@/components/common/BreadTabulatorFormSelectModel.vue";
import BreadTabulatorFormDependanceSelectModel from "@/components/common/BreadTabulatorFormDependanceSelectModel.vue";
import BreadTabulatorFormParentSelectModel from "@/components/common/BreadTabulatorFormParentSelectModel.vue";
import BreadTabulatorFormChildSelectModel from "@/components/common/BreadTabulatorFormChildSelectModel.vue";
import Form from "@/objects/form";
import { mapState } from "vuex";

/** These render more than one input, so they never share a row. */
const FULL_WIDTH_TYPES = [
  "textarea",
  "modelDependency",
  "modelParent",
  "modelChild",
];

/**
 * These carry two controls with a name each, so the form can't put one label
 * above the pair — they keep the labels their own component renders.
 */
const OWN_LABEL_EXEMPT_TYPES = [
  "boolean",
  "modelDependency",
  "modelParent",
  "modelChild",
];

export default {
  name: "BreadModelForm",
  components: {
    BreadTabulatorFormSelectModel,
    BreadTabulatorFormDependanceSelectModel,
    BreadTabulatorFormParentSelectModel,
    BreadTabulatorFormChildSelectModel,
  },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    sectionTitle() {
      return this.action === "edit" ? "Record details" : "New record";
    },
    subtitle() {
      const n = this.form.fields.length;
      return `${n} ${n === 1 ? "field" : "fields"}${
        this.requiredCount ? `, ${this.requiredCount} required` : ""
      }`;
    },
    submitLabel() {
      return this.action === "edit" ? "Save changes" : "Create";
    },
    requiredCount() {
      return Object.values(this.form.rules || {}).filter((r) =>
        (r || []).includes("required")
      ).length;
    },
    /** Keeps the skeleton the same height as the form it stands in for. */
    skeletonCount() {
      return Math.max(this.form.fields.length, 2);
    },
    /**
     * Which fields span the full row. Beyond the types that always do, the LAST
     * field spans it too when it would otherwise start a row on its own — an
     * odd field count used to leave a half-row of empty dialog beside it, which
     * is the hole the two-column grid was being blamed for.
     */
    wideIndexes() {
      const wide = new Set();
      let rowFilled = false; // a half-row is open above the current field

      this.form.fields.forEach((field, index) => {
        const isLast = index === this.form.fields.length - 1;

        if (this.isFullWidth(field)) {
          wide.add(index);
          rowFilled = false;
          return;
        }

        if (isLast && !rowFilled) wide.add(index);
        rowFilled = !rowFilled;
      });

      return wide;
    },
  },
  props: {
    action: {
      type: String,
      required: true,
    },
    api: {
      type: Object,
      required: true,
    },
    data: {
      type: Object,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Drop the card chrome — heading, and the Cancel/Save footer — and render
     * the field grid alone, for a host that supplies its own (the read page's
     * edit dialog). The host drives submission through `validate()`, which is
     * the same entry point the footer button uses, so nothing about what
     * reaches the API differs between the two modes.
     */
    embedded: {
      type: Boolean,
      default: false,
    },
    model: {
      type: Object,
      required: true,
    },
    parentModel: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    form: new Form(),
    rules: {},
    parentSelectedValue: null,
  }),
  created() {
    this.form.get({ model: this.model, action: this.action });
    this.createRules();
    this.setDefaultValueForSelectModel();
  },
  methods: {
    setData(data) {
      this.form.setData(data);
    },
    setDataProperty(key, value) {
      this.form.setDataProperty(key, value);
    },
    getParentSelectedItem(parentItem) {
      this.parentSelectedValue = parentItem;
    },
    rulesFor(key) {
      return this.rules[key] ? this.rules[key] : [];
    },
    isRequired(key) {
      return (this.form.rules[key] || []).includes("required");
    },
    labelFor(field) {
      return this.isRequired(field.key) ? `${field.name} *` : field.name;
    },
    isFullWidth(field) {
      return FULL_WIDTH_TYPES.includes(field.type);
    },
    isWide(index) {
      return this.wideIndexes.has(index);
    },
    hasOwnLabel(field) {
      return !OWN_LABEL_EXEMPT_TYPES.includes(field.type);
    },
    /**
     * On a child-add form the parent is fixed by the route, so its selector is
     * shown but locked. Same expression BreadTabulatorForm used, lifted out of
     * the template.
     */
    isParentLockedField(field) {
      if (!this.parentModel) return false;
      const lockedKey = this.parentModel.column
        ? `${this.parentModel.key}${this.$_ucfirst(this.parentModel.column.uid)}`
        : `${this.parentModel.key}Id`;
      return field.key === lockedKey;
    },
    setDefaultValueForSelectModel() {
      this.form.fields.forEach((field) => {
        if (field.type === "select") {
          var selectedOption = field.options.find(
            (option) => option.selected === true
          );
          this.form.data[field.key] = selectedOption;
        }
      });
    },
    validate() {
      this.api.callbackReset();
      if (this.$refs.form.validate()) {
        this.submit();
      } else {
        this.api.callbackError("Please check the form.");
      }
    },
    submit() {
      var data = this.$_.clone(this.form.data);

      this.form.fields.forEach((field) => {
        if (field.type === "number") {
          data[field.key] = parseInt(this.form.data[field.key], 10);
        }

        if (field.type === "float") {
          data[field.key] = parseFloat(this.form.data[field.key]);
        }

        if (field.type === "date") {
          data[field.key] = new Date(this.form.data[field.key]);
        }

        if (field.type === "dependenceModel") {
          data[field.parentKey] = this.form.data[field.parentKey];
          data[field.childKey] = this.form.data[field.childKey];
        }
      });

      if (data._count) delete data._count;
      if (this.model.parents) {
        for (var parent of this.model.parents) {
          delete data[this.$_ucfirst(parent)];
        }
      }

      if (this.model.key == "leadNew") {
        data["internalUserUuid"] = this.auth.uuid;

        if (typeof data["status"] == "string") {
          data["status"] = this.form.data["status"];
        }

        if (typeof data["status"] == "object") {
          data["status"] = this.form.data["status"].value;
        }
      }

      data = JSON.stringify(data);
      this.api.setParams({
        modelKey: this.model.key,
        data: data,
      });
      this.api.fetch();
    },
    createRules() {
      var rules = {};
      var validations = {
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid email.";
        },
        mobile: (value) => {
          const pattern = /^[+()-\d]+$/;
          return pattern.test(value) || "Invalid mobile number.";
        },
        required: (value) => !!value || "Required.",
      };

      for (const [key, value] of Object.entries(this.form.rules)) {
        rules[key] = [];
        for (const ruleKey of value) {
          rules[key].push(validations[ruleKey]);
        }
      }
      this.rules = rules;
    },
  },
};
</script>

<style scoped>
/* Token-only colour (--app-* / --tbl-*), so light and dark are both covered
   without a companion `.dark-mode` block. */
.bmf__card {
  border-radius: 12px;
  overflow: hidden;
  background: var(--app-card) !important;
}

/* Embedded, the dialog owns the surface — the card must not paint a second one
   over it, or the body reads as a panel floating inside a panel. The v-sheet
   compounds are there to out-specify Vuetify's own dark-theme sheet colour,
   which is a near-black that would otherwise sit under the whole field grid. */
.bmf__card--embedded,
.bmf__card--embedded.v-sheet.theme--dark,
.bmf__card--embedded.v-sheet.theme--light {
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 0;
}

.bmf__card--embedded .bmf__body {
  padding: 0;
  background: transparent;
}

.bmf__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--app-border);
}

.bmf__title {
  font-family: var(--tbl-font);
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--app-text);
  margin: 0;
}

.bmf__subtitle {
  font-family: var(--tbl-font);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--app-text-faint);
  margin: 3px 0 0;
}

/* The form area sits one step BACK from the card's chrome, which is what gives
   the field wells a tier to rise out of — see the dialog's block in
   PageServiceModelRead.vue for the full three-tier reasoning. Embedded, the
   host paints this instead. */
.bmf__body {
  padding: 20px;
  background: var(--app-surface);
}

/* ── Grid ─────────────────────────────────────────────────────────────────
   A real two-column grid rather than v-row/v-col: the gutters are then a
   single `gap` instead of per-column padding plus the row's negative margin,
   which is what made the fields sit tight against the dialog's own padding on
   one side and loose on the other. Rows are also independent of each other, so
   a tall field can't push its neighbour's baseline down. */
.bmf__grid {
  /* Height of one label row — the offset a field without a label of its own
     needs to sit on the same line as the boxes beside it. */
  --bmf-label-offset: 23px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 18px;
}

.bmf__field--wide {
  grid-column: 1 / -1;
}

/* A checkbox wears its label beside it rather than above, so it starts at the
   top of its cell and lands a label's height above its row-mates. */
.bmf__field--check {
  padding-top: var(--bmf-label-offset);
}

.bmf__label {
  display: block;
  font-family: var(--tbl-font);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--app-text-muted);
  margin: 0 0 6px 2px;
}

.bmf__req {
  color: var(--app-danger);
  font-weight: 800;
}

.bmf__skeleton >>> .v-skeleton-loader__text {
  background: var(--app-elevated) !important;
}

/* ── One field chrome for every input in the form ─────────────────────────
   The shared BreadTabulatorFormSelectModel family is asked for the outlined /
   dense / hide-details chrome by prop (see the template), so its selects are
   the same 40px box as the text fields beside them. Painting them from here
   instead was what left the select 16px lower and its column 30px taller than
   its row-mate: an underline v-select reserves label space above its slot and
   a details slot below it, and no amount of container CSS removes either. The
   underline rules further down stay as a fallback for anything that still
   renders one. */
.bmf__body >>> .v-input {
  font-family: var(--tbl-font);
}

/* A field type that renders more than one input (modelDependency) stacks them
   in one column; with the details slot gone they would otherwise touch. */
.bmf__body >>> .v-input + .v-input {
  margin-top: 12px;
}

/* Checkboxes carry Vuetify's selection-control offsets, which put them off the
   baseline of the boxes they share a row with. */
.bmf__body >>> .v-input--checkbox.v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
  min-height: 40px;
  align-items: center;
}

/* `top: 0` closes the gap along the top of every field. Vuetify lifts the
   outlined border 5px above the input's own box so a floating label has a notch
   to sit in; the fill is painted on the box, so those 5px stayed the colour of
   the form area — a strip of background inside the border, above the field.
   With the labels drawn above the box there is no notch to leave room for. */
.bmf__body >>> .v-text-field--outlined fieldset {
  top: 0;
  border-color: var(--app-field-border);
  border-radius: 10px;
  transition: border-color 0.15s ease;
}

/* The multi-input types still float their own labels, so they keep the room. */
.bmf__field--inner-label >>> .v-text-field--outlined fieldset {
  top: -5px;
}

/* One fill for every box. Without this the dark theme paints outlined slots
   --app-elevated, a tier below the field tokens the rest of the form's inputs
   use, so half the boxes in a row read a shade darker than the other half. */
.bmf__body >>> .v-text-field--outlined > .v-input__control > .v-input__slot {
  background: var(--app-field);
  border-radius: 10px;
}

.bmf__body >>> .v-text-field--outlined:hover fieldset {
  border-color: var(--app-field-border-hover);
}

/* Focus is the accent border plus a ring, rather than Vuetify's 2px border —
   a border that changes width nudges the text inside it by a pixel. */
.bmf__body >>> .v-input--is-focused.v-text-field--outlined fieldset {
  border-color: var(--app-accent) !important;
  border-width: 1px;
}

.bmf__body >>> .v-input--is-focused.v-text-field--outlined > .v-input__control > .v-input__slot {
  box-shadow: 0 0 0 3px var(--app-ring);
}

.bmf__body >>> .v-input--has-state.v-text-field--outlined fieldset {
  border-color: var(--app-danger) !important;
}

.bmf__body >>> .v-input input,
.bmf__body >>> .v-input textarea,
.bmf__body >>> .v-select__selection {
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--app-text);
}

.bmf__body >>> .v-input__append-inner .v-icon {
  font-size: 20px;
  color: var(--app-text-faint);
}

/* Validation messages sit under their own field, in the row's gap. */
.bmf__body >>> .v-messages__message {
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.35;
  padding-top: 4px;
}

.bmf__body >>> .v-label {
  font-size: 0.86rem;
  color: var(--app-field-placeholder);
}

/* The sub-components' selects are underline-style; give them the same box so
   the grid reads as one form rather than two eras of it. */
.bmf__body >>> .v-input:not(.v-text-field--outlined):not(.v-input--checkbox) .v-input__slot {
  background: var(--app-field);
  border: 1px solid var(--app-field-border);
  border-radius: 10px;
  min-height: 40px;
  padding: 0 12px;
  margin-bottom: 4px;
}

.bmf__body >>> .v-input:not(.v-text-field--outlined):not(.v-input--checkbox) .v-input__slot:hover {
  border-color: var(--app-field-border-hover);
}

.bmf__body >>> .v-input:not(.v-text-field--outlined):not(.v-input--checkbox) .v-input__slot::before,
.bmf__body >>> .v-input:not(.v-text-field--outlined):not(.v-input--checkbox) .v-input__slot::after {
  display: none;
}

.bmf__check >>> .v-label {
  font-weight: 600;
  color: var(--app-text);
}

/* ── Actions ─────────────────────────────────────────────────────────────── */
.bmf__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--app-border);
  background: var(--app-card);
}

.bmf__hint {
  flex: 1;
  font-family: var(--tbl-font);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--app-text-faint);
}

.bmf__btn {
  height: 38px;
  padding: 0 20px;
  border-radius: 10px;
  font-family: var(--tbl-font);
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  transition: all 0.15s ease;
}

/* Transparent for the same reason as the dialog's: the bar it stands on is
   --app-card now. */
.bmf__btn--ghost {
  border: 1px solid var(--app-border-strong);
  background: transparent;
  color: var(--app-text-muted);
}

.bmf__btn--ghost:hover {
  background: var(--app-hover);
  border-color: var(--app-border-strong);
  color: var(--app-text);
}

.bmf__btn--primary.v-btn {
  background: var(--app-accent) !important;
  color: var(--app-accent-fg) !important;
  box-shadow: none;
}

.bmf__btn--primary.v-btn:hover {
  background: var(--app-accent-strong) !important;
}

@media (max-width: 599px) {
  .bmf__body {
    padding: 14px 12px;
  }

  /* One column: two 40px boxes side by side on a phone leave neither room to
     read its own value. */
  .bmf__grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  /* Nothing shares its row to line up with any more. */
  .bmf__field--check {
    padding-top: 0;
  }

  .bmf__head,
  .bmf__actions {
    padding: 12px;
  }

  .bmf__hint {
    display: none;
  }

  .bmf__btn {
    flex: 1;
  }
}
</style>
